import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

import { getUserProfileCard } from "../../services/userProfileCard";
import "../../scss/components/_userProfileCard.scss";

const PROFILE_REFRESH_MS = 30 * 1000;

const getUserName = (profile = {}) => {
    const name = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
    return name || profile.username || "Пользователь";
};

const getInitials = (profile = {}) => {
    const first = String(profile.firstName || "").trim()[0] || "";
    const last = String(profile.lastName || "").trim()[0] || "";
    return (first || last ? `${first}${last}` : String(profile.username || "U")[0] || "U").toUpperCase();
};

const formatClock = (date) => new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
}).format(date);

export const formatProfilePresence = (presence = {}) => {
    if (presence?.online) return "в сети";
    if (!presence?.lastSeenAt) return "последняя активность пока не зафиксирована";

    const date = new Date(presence.lastSeenAt);
    const timestamp = date.getTime();
    if (!Number.isFinite(timestamp)) return "последняя активность пока не зафиксирована";

    const now = new Date();
    const diffMs = Math.max(0, now.getTime() - timestamp);
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return "был(а) в сети только что";
    if (diffMinutes < 60) return `был(а) в сети ${diffMinutes} мин назад`;

    const sameDay = now.getFullYear() === date.getFullYear() &&
        now.getMonth() === date.getMonth() &&
        now.getDate() === date.getDate();

    if (sameDay) return `был(а) в сети сегодня в ${formatClock(date)}`;

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const wasYesterday = yesterday.getFullYear() === date.getFullYear() &&
        yesterday.getMonth() === date.getMonth() &&
        yesterday.getDate() === date.getDate();

    if (wasYesterday) return `был(а) в сети вчера в ${formatClock(date)}`;

    return `был(а) в сети ${new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date)}`;
};

const OFFICE_ROLE_LABELS = {
    owner: "Владелец",
    admin: "Администратор",
    manager: "Менеджер",
    sales: "Продажи",
    installer: "Монтажник",
    driver: "Водитель",
    warehouse: "Склад",
    accountant: "Бухгалтер",
    worker: "Сотрудник",
};

const ACCOUNT_ROLE_LABELS = {
    user: "Пользователь",
    admin: "Администратор",
    partner: "Партнёр",
    superAdmin: "Супер-администратор",
    storeOwner: "Владелец магазина",
    officeWorker: "Сотрудник офиса",
    cityAdmin: "Городской администратор",
};

export default function UserProfileModal({
    open,
    userId,
    chatId = null,
    officeId = null,
    initialData = null,
    onLoaded = null,
    onClose,
}) {
    const [data, setData] = useState(initialData || null);
    const [loading, setLoading] = useState(!initialData);
    const [error, setError] = useState("");
    const [copiedKey, setCopiedKey] = useState("");
    const copyTimerRef = useRef(null);

    useEffect(() => {
        setData(initialData || null);
    }, [initialData, userId]);

    useEffect(() => {
        if (!open || !userId) return undefined;

        let cancelled = false;
        let timer = null;

        const load = async ({ silent = false } = {}) => {
            if (!silent) setLoading(true);

            try {
                const next = await getUserProfileCard(userId, { chatId, officeId });
                if (cancelled) return;

                setData(next);
                setError("");
                onLoaded?.(next);
            } catch (requestError) {
                if (cancelled) return;

                setError(
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    "Не удалось загрузить профиль"
                );
            } finally {
                if (!cancelled && !silent) setLoading(false);
            }
        };

        load();
        timer = window.setInterval(() => load({ silent: true }), PROFILE_REFRESH_MS);

        return () => {
            cancelled = true;
            if (timer) window.clearInterval(timer);
        };
    }, [open, userId, chatId, officeId, onLoaded]);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) setCopiedKey("");

        return () => {
            if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
        };
    }, [open]);

    const copyValue = async (key, value) => {
        const text = String(value || "").trim();
        if (!text) return;

        let copied = false;

        try {
            if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                copied = true;
            } else if (typeof document !== "undefined") {
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.setAttribute("readonly", "");
                textarea.style.position = "fixed";
                textarea.style.opacity = "0";
                document.body.appendChild(textarea);
                textarea.select();
                copied = document.execCommand("copy");
                document.body.removeChild(textarea);
            }
        } catch (_error) {
            copied = false;
        }

        if (!copied) return;

        setCopiedKey(key);
        if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
        copyTimerRef.current = window.setTimeout(() => setCopiedKey(""), 1400);
    };

    const renderCopyButton = (key, value, label) => (
        <button
            type="button"
            className={`user-profile-card-AO__copy ${copiedKey === key ? "is-copied" : ""}`}
            onClick={() => copyValue(key, value)}
            aria-label={copiedKey === key ? "Скопировано" : `Скопировать ${label}`}
            title={copiedKey === key ? "Скопировано" : `Скопировать ${label}`}
        >
            {copiedKey === key ? <CheckOutlinedIcon /> : <ContentCopyOutlinedIcon />}
        </button>
    );

    const profile = data?.profile || {};
    const presence = data?.presence || {};
    const office = data?.office || null;
    const title = useMemo(() => getUserName(profile), [profile]);
    const initials = useMemo(() => getInitials(profile), [profile]);
    const presenceLabel = formatProfilePresence(presence);

    if (!open || typeof document === "undefined") return null;

    const modal = (
        <div className="user-profile-card-AO__backdrop" onMouseDown={onClose}>
            <section
                className="user-profile-card-AO"
                role="dialog"
                aria-modal="true"
                aria-label={`Профиль: ${title}`}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="user-profile-card-AO__head">
                    <span>Профиль пользователя</span>
                    <button type="button" onClick={onClose} aria-label="Закрыть">
                        <CloseOutlinedIcon />
                    </button>
                </div>

                {loading && !data ? (
                    <div className="user-profile-card-AO__state">
                        <span className="user-profile-card-AO__spinner" />
                        Загружаем профиль…
                    </div>
                ) : error && !data ? (
                    <div className="user-profile-card-AO__state is-error">{error}</div>
                ) : (
                    <>
                        <div className="user-profile-card-AO__hero">
                            <div className="user-profile-card-AO__avatar">
                                {profile.profilePicture ? (
                                    <img src={profile.profilePicture} alt="" />
                                ) : (
                                    <span>{initials}</span>
                                )}
                                <i className={presence.online ? "is-online" : ""} />
                            </div>

                            <div className="user-profile-card-AO__identity">
                                <h3>{title}</h3>
                                {profile.username ? (
                                    <div className="user-profile-card-AO__username-line">
                                        <span>@{profile.username}</span>
                                        {renderCopyButton("username", `@${profile.username}`, "ник")}
                                    </div>
                                ) : null}
                                <p className={presence.online ? "is-online" : ""}>{presenceLabel}</p>
                            </div>
                        </div>

                        <div className="user-profile-card-AO__details">
                            {office?.role ? (
                                <div className="user-profile-card-AO__row">
                                    <b>Роль в офисе</b>
                                    <span>{OFFICE_ROLE_LABELS[office.role] || office.role}</span>
                                </div>
                            ) : null}

                            {profile.accountRole ? (
                                <div className="user-profile-card-AO__row">
                                    <b>Роль аккаунта</b>
                                    <span>{ACCOUNT_ROLE_LABELS[profile.accountRole] || profile.accountRole}</span>
                                </div>
                            ) : null}

                            {Object.prototype.hasOwnProperty.call(profile, "isActive") ? (
                                <div className="user-profile-card-AO__row">
                                    <b>Статус аккаунта</b>
                                    <span>{profile.isActive ? "Активен" : "Отключён"}</span>
                                </div>
                            ) : null}

                            {profile.email ? (
                                <div className="user-profile-card-AO__contact">
                                    <MailOutlineOutlinedIcon />
                                    <a className="user-profile-card-AO__contact-main" href={`mailto:${profile.email}`}>
                                        <b>Email</b><span>{profile.email}</span>
                                    </a>
                                    {renderCopyButton("email", profile.email, "email")}
                                </div>
                            ) : null}

                            {profile.contactNumber ? (
                                <div className="user-profile-card-AO__contact">
                                    <PhoneOutlinedIcon />
                                    <a className="user-profile-card-AO__contact-main" href={`tel:${profile.contactNumber}`}>
                                        <b>Телефон</b><span>{profile.contactNumber}</span>
                                    </a>
                                    {renderCopyButton("phone", profile.contactNumber, "телефон")}
                                </div>
                            ) : null}

                            {profile.nameOrganization ? (
                                <div className="user-profile-card-AO__contact is-static">
                                    <BusinessOutlinedIcon />
                                    <div className="user-profile-card-AO__contact-main">
                                        <b>Организация</b><span>{profile.nameOrganization}</span>
                                    </div>
                                    {renderCopyButton("organization", profile.nameOrganization, "организацию")}
                                </div>
                            ) : null}

                            {profile.regionName ? (
                                <div className="user-profile-card-AO__contact is-static">
                                    <PlaceOutlinedIcon />
                                    <div className="user-profile-card-AO__contact-main">
                                        <b>Регион</b><span>{profile.regionName}</span>
                                    </div>
                                    {renderCopyButton("region", profile.regionName, "регион")}
                                </div>
                            ) : null}
                        </div>

                        {error ? <div className="user-profile-card-AO__refresh-error">{error}</div> : null}

                        <div className="user-profile-card-AO__privacy-note">
                            Показываются только данные, доступные вам по правам доступа.
                        </div>
                    </>
                )}
            </section>
        </div>
    );

    return createPortal(modal, document.body);
}
